const { flatMap } = require('../../utils/Array');
const config = require('../config.js');
const pools = require(`./pools-${config.blockchainId}.js`);
const validatePoolConfigs = require('./init-validation');
const REFERENCE_ASSETS = require('../reference-assets.json');

const defaultPoolTemplate = {
    dataIndex: undefined,
    id: undefined,
    idAlias: undefined, // e.g. how 'y' and 'iearn' are sometimes interchangeable
    name: undefined,
    pageMetaData: {
        title: undefined, // Defaults to `${pool.name}`
        description: undefined, // Defaults to `A curve.fi portal for swapping ${pool.assets}`
    },
    lpTokenInfo: {
        name: undefined,
        symbol: undefined,
    },
    coingeckoInfo: {
        id: undefined, // Must be defined
        symbol: undefined, // Must be defined
        referenceAssetId: 'dollar',
    },
    assets: undefined,
    coins: undefined,
    underlyingCoins: undefined, // Must be set when isLendingPool=true
    metaCoins: undefined, // Must be set when isMetaPool=true
    referenceAsset: REFERENCE_ASSETS.USD, // Pool type, from the enum REFERENCE_ASSETS
    isLendingPool: false, // True when underlying assets are lent on Compound/Aave/else
    isModernLendingPool: false, // aave, saave, ib, and future lending pools
    isMetaPool: false,
    isRiskier: false, // True for pools labeled as "innovation zone"
    riskLevel: undefined, // Must be defined if isRiskier = true
    // Shape: { name, rewardTokenCoingeckoId, amountDataKey } for gaugeVersion === 1
    // Shape: { name, rewardTokenCoingeckoId, amountDataKey, rewardTokenAddress, rewardTokenDecimals } for gaugeVersion === 2
    additionalRewards: [],
    // Same as additionalRewards, only those rewards aren't actively distributed anymore.
    // Those old rewards aren't displayed in the pool list anymore, but this info is used
    // to retrieve claimable amounts for users who partook in these.
    // Shape: { name, rewardTokenCoingeckoId } for gaugeVersion === 1
    // Shape: { name, rewardTokenCoingeckoId, rewardTokenAddress, rewardTokenDecimals } for gaugeVersion === 2
    oldAdditionalRewards: [],
    hasNoGauge: false,
    gaugeVersion: null,
    isPendingGaugeVoteToStartCrvRewards: false,
    expectedCrvRewardsStart: null,
    addresses: {
        swap: null,
        lpToken: null,
        gauge: null,
        deposit: null,
        stakingRewards: null,
        adapter: null,
        migrator: null //if pool is being migrated to something else
    },
    hasAMultiplier: true,
    isOldPool: false,
    hasNoCRVRewards: false, //for pools that may have a gauge but no CRV Rewards like on Polygon/Matic
    poolType: 'stable', //stable or crypto
    isFakePlainPool: false //this is a hacky bit to have the crypto pool play off as plain even though it is meta lending and has a zap
};

const augmentedPools = pools.map((pool) => ({
    ...defaultPoolTemplate,
    ...pool,
    // Default to 1 for pools with gauges
    gaugeVersion: (pool.gaugeVersion || pool.hasNoGauge) ? pool.gaugeVersion : 1,
    coingeckoInfo: { // Deep merge
        ...defaultPoolTemplate.coingeckoInfo,
        ...pool.coingeckoInfo,
    },
    addresses: { // Deep merge
        ...defaultPoolTemplate.addresses,
        ...pool.addresses,
    },
    pageMetaData: { // Deep merge
        title: pool.name,
        description: `A curve.fi portal for swapping ${pool.assets}`,
        ...pool.pageMetaData,
    },
    get containsSynthCoin() {
        return (
            pool.coins.some(({ isSynth }) => isSynth)
            // Mapping for metapools not available yet in synth swaps, uncomment below + delete last line when it is
            // || !!pool.underlyingCoins?.some(({ isSynth }) => isSynth)
            // || !!pool.metaCoins?.some(({ isSynth }) => isSynth)
            && !pool.isMetaPool
        );
    },
    get containsAaveCoin() {
        return (
            pool.coins.some(({ wrappedCoinType }) => wrappedCoinType === 'aave')
        );
    },
    get containsCompoundCoin() {
        return (
            pool.coins.some(({ wrappedCoinType }) => wrappedCoinType === 'compound')
        );
    },
    get containsYearnBasedCoin() {
        return (
            pool.coins.some(({ wrappedCoinType }) => wrappedCoinType && wrappedCoinType.startsWith('iearn'))
        );
    },
})).sort((a, b) => (
    a.dataIndex < b.dataIndex ? -1 :
    a.dataIndex > b.dataIndex ? 1 : 0
));

validatePoolConfigs(augmentedPools);

const poolIds = augmentedPools.map(({ id }) => id);
const poolIdsWithAliases = flatMap(augmentedPools, ({ id, idAlias }) => [id, idAlias]).filter((o) => !!o);
const poolGauges = augmentedPools.map(({ addresses: { gauge } }) => gauge).filter((gauge) => !!gauge);

const exp = augmentedPools;
exp.poolIds = poolIds;
exp.poolIdsWithAliases = poolIdsWithAliases;
exp.poolGauges = poolGauges;

exp.getById = (id) => (
    augmentedPools.find((pool) => id === pool.id) ||
    augmentedPools.find((pool) => id === pool.idAlias) // Check for id aliases if no match for any pool id
);

exp.findPoolForCoinsIds = (coinIdA, coinIdB) => augmentedPools.find(({ coins, underlyingCoins }) => (
    (coins.some(({ id }) => id === coinIdA) || (underlyingCoins && underlyingCoins.some(({ id }) => id === coinIdA))) &&
    (coins.some(({ id }) => id === coinIdB) || (underlyingCoins && underlyingCoins.some(({ id }) => id === coinIdB)))
));

exp.findSynthPoolWithCoinId = (coinId) => augmentedPools.find((pool) => ((
    pool.coins.some(({ id }) => id === coinId) ||
    (pool.underlyingCoins && pool.underlyingCoins.some(({ id }) => id === coinId))
) && pool.containsSynthCoin));

exp.findPoolForSwapAddress = (swapAddress) => {
    const lcSwapAddress = swapAddress.toLowerCase();
    return augmentedPools.find(({ addresses }) => (
        addresses.swap.toLowerCase() === lcSwapAddress ||
        (addresses.deposit && addresses.deposit.toLowerCase() === lcSwapAddress)
    ));
};

module.exports = exp;
